/**
 * Get timezone from postal/zip code
 * Uses common mappings for US and Canadian postal codes
 */

// Common timezone mappings for postal codes
const US_TIMEZONE_MAP: Record<string, string> = {
  // Eastern Time (ET)
  '10001': 'America/New_York', // NYC
  '02101': 'America/New_York', // Boston
  '30301': 'America/New_York', // Atlanta
  '33101': 'America/New_York', // Miami
  // Central Time (CT)
  '60601': 'America/Chicago', // Chicago
  '60087': 'America/Chicago', // Waukegan, IL
  '75201': 'America/Chicago', // Dallas
  '77001': 'America/Chicago', // Houston
  // Mountain Time (MT)
  '80201': 'America/Denver', // Denver
  '84101': 'America/Denver', // Salt Lake City
  '85001': 'America/Phoenix', // Phoenix (no DST)
  // Pacific Time (PT)
  '90001': 'America/Los_Angeles', // Los Angeles
  '94101': 'America/Los_Angeles', // San Francisco
  '98101': 'America/Los_Angeles', // Seattle
};

const CANADA_TIMEZONE_MAP: Record<string, string> = {
  // Pacific Time
  'V6B': 'America/Vancouver', // Vancouver
  'V6B 6G6': 'America/Vancouver',
  // Mountain Time
  'T2P': 'America/Edmonton', // Calgary
  'T5J': 'America/Edmonton', // Edmonton
  // Central Time
  'R3T': 'America/Winnipeg', // Winnipeg
  'R3T 6A8': 'America/Winnipeg',
  // Eastern Time
  'M5V': 'America/Toronto', // Toronto
  'M5V 3L9': 'America/Toronto',
  'H3A': 'America/Toronto', // Montreal
  'K1A': 'America/Toronto', // Ottawa
  // Atlantic Time
  'B3H': 'America/Halifax', // Halifax
};

/** Canadian postal codes start with a letter; US ZIP codes are numeric. */
export function inferPostalCountry(
  postalCode: string | null | undefined,
): "US" | "CA" {
  const normalized = String(postalCode || "").replace(/\s+/g, "").toUpperCase();
  if (/^[A-Z]/.test(normalized)) return "CA";
  return "US";
}

export type ProjectDefaultTimezone = {
  timezone: string | null;
  locationName: string | null;
  zipCode: string | null;
};

/** Default project location → IANA timezone (for Calendly, orchestrator, etc.). */
export function resolveProjectDefaultTimezone(
  locations:
    | Array<{ zipCode?: string | null; isDefaultLocation?: boolean; name?: string }>
    | null
    | undefined,
): ProjectDefaultTimezone {
  if (!locations?.length) {
    return { timezone: null, locationName: null, zipCode: null };
  }
  const defaultLocation =
    locations.find((loc) => loc.isDefaultLocation) ?? locations[0];
  const zipCode = defaultLocation?.zipCode?.trim() || null;
  if (!zipCode) {
    return {
      timezone: null,
      locationName: defaultLocation?.name ?? null,
      zipCode: null,
    };
  }
  const country = inferPostalCountry(zipCode);
  return {
    timezone: getTimezoneFromPostalCode(zipCode, country),
    locationName: defaultLocation?.name ?? null,
    zipCode,
  };
}

/** Visitor-facing label for a Calendly slot (API returns UTC ISO). */
export function formatCalendlySlotForDisplay(
  isoUtc: string,
  timezone: string,
): {
  start_time: string;
  timezone: string;
  local_date: string;
  local_time: string;
  local_datetime_label: string;
} {
  const parsed = new Date(isoUtc);
  const start_time = Number.isNaN(parsed.getTime()) ? isoUtc : parsed.toISOString();
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const fullFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dateParts = dateFormatter.formatToParts(parsed);
  const year = dateParts.find((p) => p.type === "year")?.value ?? "";
  const month = dateParts.find((p) => p.type === "month")?.value ?? "";
  const day = dateParts.find((p) => p.type === "day")?.value ?? "";
  const local_date = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const local_time = timeFormatter.format(parsed);
  return {
    start_time,
    timezone,
    local_date,
    local_time,
    local_datetime_label: fullFormatter.format(parsed),
  };
}

/**
 * Get timezone from postal code
 * @param postalCode - Postal/zip code (can be full or partial)
 * @param country - Optional country code (defaults to 'US')
 * @returns IANA timezone string or null if not found
 */
export function getTimezoneFromPostalCode(
  postalCode: string | null | undefined,
  country: string = 'US'
): string | null {
  if (!postalCode) return null;

  // Normalize postal code (remove spaces, convert to uppercase)
  const normalized = postalCode.replace(/\s+/g, '').toUpperCase();

  // Try full match first
  if (country === 'CA' || country === 'CAN') {
    if (CANADA_TIMEZONE_MAP[normalized]) {
      return CANADA_TIMEZONE_MAP[normalized];
    }
    // Try first 3 characters for Canadian postal codes (e.g., "V6B" from "V6B 6G6")
    const prefix = normalized.substring(0, 3);
    if (CANADA_TIMEZONE_MAP[prefix]) {
      return CANADA_TIMEZONE_MAP[prefix];
    }
  } else {
    // US zip codes
    if (US_TIMEZONE_MAP[normalized]) {
      return US_TIMEZONE_MAP[normalized];
    }
    // Try first 3 digits for US zip codes
    const prefix = normalized.substring(0, 3);
    if (US_TIMEZONE_MAP[prefix]) {
      return US_TIMEZONE_MAP[prefix];
    }
  }

  // Fallback: Use common timezone mappings based on postal code patterns
  // Canadian postal codes start with letters
  if (/^[A-Z]/.test(normalized)) {
    // British Columbia (V)
    if (normalized.startsWith('V')) {
      return 'America/Vancouver';
    }
    // Alberta (T)
    if (normalized.startsWith('T')) {
      return 'America/Edmonton';
    }
    // Manitoba/Saskatchewan (R)
    if (normalized.startsWith('R')) {
      return 'America/Winnipeg';
    }
    // Ontario/Quebec (M, K, H, L, N, P, G, J)
    if (/^[MKHLNPJG]/.test(normalized)) {
      return 'America/Toronto';
    }
    // Atlantic provinces (B, E, C)
    if (/^[BEC]/.test(normalized)) {
      return 'America/Halifax';
    }
  }

  // US zip codes are numeric
  if (/^\d/.test(normalized)) {
    const firstDigit = normalized.charAt(0);
    // Rough mapping by first digit (not perfect but better than nothing)
    if (['0', '1', '2', '3', '4'].includes(firstDigit)) {
      return 'America/New_York'; // Eastern
    }
    if (['5', '6', '7'].includes(firstDigit)) {
      return 'America/Chicago'; // Central
    }
    if (firstDigit === '9' && normalized.startsWith('9')) {
      return 'America/Los_Angeles'; // Pacific (CA, WA, OR)
    }
    if (firstDigit === '8') {
      return 'America/Denver'; // Mountain (rough)
    }
  }

  return null;
}

/**
 * Get current date and time in a specific timezone
 * @param timezone - IANA timezone string (e.g., 'America/Toronto')
 * @returns Object with date, time, and formatted datetime
 */
export function getDateTimeInTimezone(timezone: string) {
  const now = new Date();
  
  // Format date in timezone
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  
  const dayNameFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  });
  
  const fullFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  const dateParts = dateFormatter.formatToParts(now);
  const timeParts = timeFormatter.formatToParts(now);
  
  const year = dateParts.find(p => p.type === 'year')?.value || '';
  const month = dateParts.find(p => p.type === 'month')?.value || '';
  const day = dateParts.find(p => p.type === 'day')?.value || '';
  
  const hour = timeParts.find(p => p.type === 'hour')?.value || '';
  const minute = timeParts.find(p => p.type === 'minute')?.value || '';
  const dayPeriod = timeParts.find(p => p.type === 'dayPeriod')?.value || '';
  
  const currentDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const currentTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')} ${dayPeriod.toUpperCase()}`;
  const currentDayName = dayNameFormatter.format(now);
  const currentDateTime = fullFormatter.format(now);

  return {
    date: currentDate,
    time: currentTime,
    dayName: currentDayName,
    dateTime: currentDateTime,
    timezone,
  };
}
